import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { invalidateFinancialQueries } from '@/hooks/queryInvalidation';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage, isNetworkError, getNetworkErrorMessage } from '@/lib/errors';
import type { BatchUpdateTransactionPatch, CreateTransactionData } from '@/lib/api';

export function usePreviewStatement() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (file: File) => api.previewStatement(file),
    onError: (error: unknown) => {
      const errorInfo = isNetworkError(error) ? getNetworkErrorMessage() : getErrorMessage(error);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}

export interface ReconcileStatementInput {
  creates: CreateTransactionData[];
  links: BatchUpdateTransactionPatch[];
  skipped: number;
}

export interface ReconcileStatementResult {
  created: number;
  linked: number;
  skipped: number;
}

export function useReconcileStatement() {
  const queryClient = useQueryClient();

  return useMutation<ReconcileStatementResult, Error, ReconcileStatementInput>({
    mutationFn: async ({ creates, links, skipped }) => {
      const tasks: Promise<unknown>[] = [];
      if (creates.length > 0) tasks.push(api.batchCreateTransactions(creates));
      if (links.length > 0) tasks.push(api.batchUpdateTransactions(links));
      await Promise.all(tasks);
      return { created: creates.length, linked: links.length, skipped };
    },
    onSuccess: () => {
      invalidateFinancialQueries(queryClient);
    },
  });
}
